import React, { useRef } from 'react'
import * as yup from 'yup'
import { Formik, FormikHelpers } from 'formik'
import { isApolloError } from 'apollo-client'

// Components
import Button from 'components/Button'
import FormikScrollToError from 'components/FormikScrollToError'
import Fieldset from '../../components/Fieldset'
import PaymentMethodForm from '../../components/PaymentMethodForm'
import { createFormikValidator } from '../../components/StripeField'
import BillingDetailsForm, {
  validationSchema,
} from '../../components/BillingDetailsForm'
import Checkbox from 'components/Checkbox'
import Link from 'components/Link'

// Styles
import {
  Form,
  Layout,
  PaymentFormContainer,
  BillingDetailsFormFormContainer,
  TermsOfServiceSection,
  BillSummarySection,
} from './WorkspaceSubscribePaymentDetailsV2.styles'
import {
  H1,
  H2,
  Text,
} from 'modules/workspace/views/WorkspaceSubscribeView/WorkspaceSubscribeView.styles'

// Formik Validation
const VALIDATION_SCHEMA = yup
  .object()
  .shape({
    cardName: yup.string().trim().required('Name on Card is required'),
    tos: yup.mixed().oneOf([true], 'Terms of service must be agreed'),
  })
  .concat(validationSchema)

const INITIAL_VALUES = {
  name: '',
  email: '',
  address: '',
  city: '',
  country: 'US',
  postalCode: '',
  cardName: '',
  taxId: '',
  state: '',
  tos: false,

  /**
   * These fields are related with the credit-card
   * data, in order to get formik like validation
   * we need to include them in the "initial values"
   *
   * these values are only placeholders, in the end,
   * none of their values will be used
   */
  cardNumber: undefined,
  cardCvc: undefined,
  cardExpirationDate: undefined,
}

const STRIPE_ERRORS_TO_FIELDS: Record<string, keyof typeof INITIAL_VALUES> = {
  incorrect_cvc: 'cardCvc',
  processing_error: 'cardNumber',
  expired_card: 'cardExpirationDate',
  card_declined: 'cardNumber',
  incorrect_number: 'cardNumber',
  tax_id_invalid: 'taxId',
}

const stripeValidation = createFormikValidator(
  ['cardCvc', 'cardExpirationDate', 'cardNumber'],
  {
    cardNumber: 'Card Number is required',
    cardCvc: 'CVC is required',
    cardExpirationDate: 'Expiry Date is required',
  }
)

type FormValues = typeof INITIAL_VALUES
type SubmitValues = OmitSafe<
  FormValues,
  'cardNumber' | 'cardCvc' | 'cardExpirationDate'
>

export interface WorkspaceSubscribePaymentDetailsProps {
  summaryUi: React.ReactElement
  onSubmit: (values: SubmitValues) => Promise<void>
}

export const WorkspaceSubscribePaymentDetails: React.FC<WorkspaceSubscribePaymentDetailsProps> = props => {
  const { onSubmit, summaryUi } = props
  const formRef = useRef<HTMLFormElement>(null)

  const handleOnSubmit = async (
    values: FormValues,
    actions: FormikHelpers<FormValues>
  ) => {
    try {
      const {
        cardCvc,
        cardExpirationDate,
        cardNumber,
        ...correctValues
      } = values

      await onSubmit(correctValues)
    } catch (error) {
      if (isApolloError(error)) {
        error.graphQLErrors.forEach(({ extensions, message }) => {
          if (message === 'Invalid value for us_ein.') {
            actions.setFieldError(
              STRIPE_ERRORS_TO_FIELDS['tax_id_invalid'],
              'Please enter a valid VAT or tax ID number.'
            )

            return
          }

          const formField = STRIPE_ERRORS_TO_FIELDS[extensions?.stripeErrorCode]

          formField && actions.setFieldError(formField, message)
        })
      }
    } finally {
      actions.setSubmitting(false)
    }
  }

  return (
    <>
      <H1>Billing Details</H1>
      <Formik
        initialValues={INITIAL_VALUES}
        validationSchema={VALIDATION_SCHEMA}
        validate={stripeValidation}
        onSubmit={handleOnSubmit}>
        {({ isSubmitting, touched, errors, handleChange, values }) => (
          <Form ref={formRef}>
            <Layout>
              <div>
                <FormikScrollToError formRef={formRef} />
                <Fieldset>
                  <H2 size="medium">Payment Method</H2>
                  <Text>Enter your card details</Text>
                  <PaymentFormContainer>
                    <PaymentMethodForm<FormValues> />
                  </PaymentFormContainer>

                  <H2 size="medium">Business Details</H2>
                  <BillingDetailsFormFormContainer>
                    <BillingDetailsForm<FormValues> />
                  </BillingDetailsFormFormContainer>
                </Fieldset>
              </div>

              <div>
                <BillSummarySection>
                  <H2 size="medium">Bill Summary</H2>
                  {summaryUi}
                </BillSummarySection>

                <TermsOfServiceSection>
                  <label htmlFor="terms-of-service" className="sr-only">
                    I’ve read and agree to the Sketch Terms of Service and order
                    details.
                  </label>
                  <Form.Field
                    name="tos"
                    errorText={touched.tos ? errors.tos : undefined}>
                    <Checkbox
                      name="tos"
                      id="terms-of-service"
                      checked={values.tos}
                      onChange={handleChange}>
                      I’ve read and agree to the Sketch{' '}
                      <Link
                        variant="secondary"
                        isUnderlined
                        external
                        href="https://www.sketch.com/tos"
                        target="_blank"
                        rel="noopener noreferrer">
                        Terms of Service
                      </Link>{' '}
                      and order details.
                    </Checkbox>
                  </Form.Field>

                  <Button
                    variant="primary-untinted"
                    fill
                    type="submit"
                    loading={isSubmitting}>
                    Confirm and Pay
                  </Button>
                </TermsOfServiceSection>
              </div>
            </Layout>
          </Form>
        )}
      </Formik>
    </>
  )
}
